import { useEffect, useRef, useState } from 'react';
import { Target, Eye, Award, Users, Lightbulb, Heart } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const values = [
  {
    icon: Lightbulb,
    title: '创新驱动',
    description: '持续探索前沿技术，以创新思维解决业务挑战',
  },
  {
    icon: Target,
    title: '客户至上',
    description: '深入理解客户需求，提供超越期望的解决方案',
  },
  {
    icon: Award,
    title: '追求卓越',
    description: '精益求精，在每个细节中追求极致品质',
  },
  {
    icon: Users,
    title: '团队协作',
    description: '汇聚顶尖人才，以团队智慧创造更大价值',
  },
  {
    icon: Heart,
    title: '诚信负责',
    description: '坚守商业道德，对客户、员工、社会负责',
  },
];

const milestones = [
  { year: '2018', event: '成都孔明智算互联网科技有限公司成立' },
  { year: '2019', event: '推出首款数字化营销产品' },
  { year: '2020', event: '服务客户突破100家' },
  { year: '2021', event: '完成A轮融资，团队扩展至200人' },
  { year: '2022', event: '大宗商品交易平台上线' },
  { year: '2023', event: '互联网生态平台正式发布，服务客户超5000家' },
  { year: '2024', event: '持续创新，构建数字化商业生态' },
];

export default function About() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-20">
        {/* Hero Section */}
        <section className="relative h-[400px] overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1920&h=600&fit=crop)`,
            }}
          />
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative h-full section-container flex items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                关于我们
              </h1>
              <p className="text-xl text-white/80 max-w-2xl">
                成都孔明智算互联网科技有限公司，构建数字化商业生态
              </p>
            </div>
          </div>
        </section>

        {/* Company Intro */}
        <section ref={sectionRef} className="py-20 bg-white">
          <div className="section-container">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Image */}
              <div
                className={`relative transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
                }`}
                style={{ transitionTimingFunction: 'var(--ease-expo-out)' }}
              >
                <div className="relative rounded-2xl overflow-hidden shadow-xl">
                  <img
                    src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&h=600&fit=crop"
                    alt="Team"
                    className="w-full h-auto"
                  />
                </div>
                {/* Decorative Element */}
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#1c5bff] rounded-2xl -z-10" />
              </div>

              {/* Content */}
              <div
                className={`transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
                }`}
                style={{
                  transitionTimingFunction: 'var(--ease-expo-out)',
                  transitionDelay: '200ms',
                }}
              >
                <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
                  构建数字化商业生态
                </h2>
                <div className="space-y-4 text-gray-600 leading-relaxed">
                  <p>
                    成都孔明智算互联网科技有限公司成立于2018年，是一家专注于数字化生态建设的高新技术企业。公司以"构建数字化商业生态"为使命，致力于为企业提供全方位的数字化解决方案。
                  </p>
                  <p>
                    经过多年的发展，公司已建立起涵盖营销中心、资源中心、分销中心、招商中心、在线商城等多个业务板块的互联网生态平台，服务超过5000家企业客户，年交易额突破100亿元。
                  </p>
                  <p>
                    公司总部位于四川省成都市武侯区九架车巷55号悦湖数园1栋，拥有一支由顶尖技术专家、行业顾问组成的专业团队，持续为客户创造价值，推动千行百业的数字化转型。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="py-20 bg-gray-50">
          <div className="section-container">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Mission */}
              <div className="bg-white rounded-2xl p-8 shadow-sm">
                <div className="w-14 h-14 bg-[#1c5bff] rounded-xl flex items-center justify-center mb-6">
                  <Target className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">我们的使命</h3>
                <p className="text-gray-600 leading-relaxed">
                  构建数字化商业生态，连接供需双方，让企业交易更简单、更高效、更安全。我们致力于成为企业最信赖的数字化转型合作伙伴。
                </p>
              </div>

              {/* Vision */}
              <div className="bg-white rounded-2xl p-8 shadow-sm">
                <div className="w-14 h-14 bg-[#1c5bff] rounded-xl flex items-center justify-center mb-6">
                  <Eye className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">我们的愿景</h3>
                <p className="text-gray-600 leading-relaxed">
                  成为中国领先的数字化商业生态平台，服务百万企业，年交易额突破千亿，推动中国商业的数字化转型升级。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-20 bg-white">
          <div className="section-container">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
                核心价值观
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                这些价值观指引着我们的每一个决策和行动
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {values.map((value) => (
                <div
                  key={value.title}
                  className="group bg-gray-50 rounded-2xl p-6 hover:bg-[#1c5bff] hover:shadow-xl transition-all duration-500"
                  style={{
                    transitionTimingFunction: 'var(--ease-expo-out)',
                  }}
                >
                  <div className="w-12 h-12 bg-[#1c5bff] rounded-xl flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110 group-hover:bg-white">
                    <value.icon className="w-6 h-6 text-white group-hover:text-[#1c5bff]" />
                  </div>
                  <h3 className="text-xl font-bold text-black mb-2 group-hover:text-white transition-colors">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 group-hover:text-white/80 transition-colors">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="py-20 bg-gray-50">
          <div className="section-container">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
                发展历程
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                见证互联网生态平台的成长与蜕变
              </p>
            </div>

            <div className="max-w-3xl mx-auto">
              {milestones.map((milestone, index) => (
                <div key={milestone.year} className="flex gap-6 md:gap-8 pb-8 last:pb-0">
                  {/* Year */}
                  <div className="flex-shrink-0 w-20 md:w-24">
                    <span className="text-xl md:text-2xl font-bold text-white bg-[#1c5bff] px-3 py-1 rounded-lg">
                      {milestone.year}
                    </span>
                  </div>

                  {/* Line */}
                  <div className="flex-shrink-0 flex flex-col items-center">
                    <div className="w-3 h-3 bg-[#1c5bff] rounded-full" />
                    {index < milestones.length - 1 && (
                      <div className="w-0.5 flex-1 bg-gray-200 mt-2" />
                    )}
                  </div>

                  {/* Event */}
                  <div className="flex-1 pb-8">
                    <p className="text-gray-700">{milestone.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="py-20 bg-white">
          <div className="section-container">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
                核心团队
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                汇聚行业精英，共创卓越价值
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  name: '陈明远',
                  position: '创始人 & CEO',
                  image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face',
                },
                {
                  name: '林雨晴',
                  position: '首席技术官',
                  image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop&crop=face',
                },
                {
                  name: '王志强',
                  position: '首席运营官',
                  image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=400&fit=crop&crop=face',
                },
              ].map((member) => (
                <div key={member.name} className="text-center group">
                  <div className="relative w-40 h-40 mx-auto mb-6">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover rounded-full border-4 border-gray-100 group-hover:border-[#1c5bff] transition-colors duration-300"
                    />
                  </div>
                  <h3 className="text-xl font-bold text-black mb-1">{member.name}</h3>
                  <p className="text-gray-500">{member.position}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Info */}
        <section className="py-20 bg-gray-50">
          <div className="section-container">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">联系我们</h2>
              <p className="text-gray-600">期待与您的合作</p>
            </div>
            <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-sm p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1c5bff]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-[#1c5bff]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">公司地址</h4>
                    <p className="text-gray-600">四川省成都市武侯区九架车巷55号悦湖数园1栋</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1c5bff]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-[#1c5bff]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">联系电话</h4>
                    <p className="text-gray-600">028-85002901</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1c5bff]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-[#1c5bff]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">电子邮箱</h4>
                    <p className="text-gray-600">contact@kongming.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1c5bff]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-[#1c5bff]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">官方网站</h4>
                    <p className="text-gray-600">www.kongming.com</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
